/* ===========================================================
   sync/docs.js — the doc list, offline-first

   The list on screen is this device's copy (IndexedDB), refreshed
   from the server whenever we're online. Things you do to the list
   (new doc, rename, trash, star) apply here at once and go into an
   outbox that's sent in order when the network allows. A new doc can
   be written in fully offline: its live connection waits until the
   outbox has created it on the server.

   Also: quietly caching recent docs in the background so they open
   instantly and can be searched and edited offline.
   =========================================================== */

import * as Y from 'yjs';
import { api, isOffline } from '../lib/api.js';
import { signal } from '@preact/signals';
import { docs, store, online, session } from '../state.js';
import { newId, unpackBundle } from '../../shared/protocol.js';
import { ydocFrom, toJSON, summary, titleOf } from '../editor/convert.js';
import { emptyDoc } from '../../shared/blocks.js';

const S = () => store.value;

/* ---------- the list ---------- */

export async function loadLocalList() {
  if (!S()) return;
  docs.value = sortDocs(await S().listDocs());
}

const sortDocs = list => list.slice().sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0));

export async function refreshList() {
  if (!S()) return;
  await flushOutbox();
  let server;
  try { server = (await api('/api/docs')).docs; } catch (err) {
    if (!isOffline(err)) throw err;
    return;
  }
  api('/api/groups').then(r => saveGroups(r.groups)).catch(() => {});
  const local = new Map((await S().listDocs()).map(d => [d.id, d]));
  const seen = new Set();
  for (const d of server) {
    seen.add(d.id);
    const mine = local.get(d.id) || {};
    // keep local-only fields (search text, pending flags); a fresher local title wins until it's sent
    const rec = { ...mine, ...d, title: mine.dirtyTitle ? mine.title : d.title, pendingCreate: false };
    if (d.kind === 'file') rec.snippet = d.snippet;   // the file's address always comes from the server
    await S().putDocRec(rec);
  }
  const pendingIds = new Set((await S().ops()).filter(o => o.op === 'create').map(o => o.id));
  for (const [id, d] of local) {
    if (seen.has(id) || pendingIds.has(id) || d.pendingCreate) continue;
    if (d.local) continue;   // imported files etc. that never go online
    await S().dropDoc(id);   // deleted elsewhere, or no longer shared with me
  }
  await loadLocalList();
}

export const findDoc = id => docs.value.find(d => d.id === id);

async function patchLocal(id, patch) {
  await S().patchDocRec(id, patch);
  docs.value = sortDocs(docs.value.map(d => (d.id === id ? { ...d, ...patch } : d)));
}

/* ---------- new docs ---------- */

/**
 * Make a doc on this device right away (with content), and queue it for the server.
 * opts: { title, json, kind: 'doc' | 'copy', parentId, baseVersion, base (JSON the copy started from) }
 */
export async function createDoc({ title = '', json = emptyDoc(), kind = 'doc', parentId = null, baseVersion = null, base = null } = {}) {
  const id = newId();
  const ydoc = ydocFrom(json, title);
  if (base) ydoc.getMap('meta').set('base', JSON.stringify(base));
  const now = Date.now();
  await S().addEntry(id, Y.encodeStateAsUpdate(ydoc), true, ydoc.clientID);
  const rec = {
    id, title, kind, parentId, baseVersion, role: 'owner', ownerId: session.value.user?.id,
    createdAt: now, updatedAt: now, pendingCreate: true, ...summary(json),
  };
  await S().putDocRec(rec);
  await S().addOp({ op: 'create', id, body: { id, title, kind, parentId, baseVersion, createdAt: now } });
  docs.value = sortDocs([rec, ...docs.value]);
  flushOutbox();
  return id;
}

/* ---------- list actions ---------- */

export async function renameDoc(id, title) {
  await patchLocal(id, { title, dirtyTitle: true, updatedAt: Date.now() });
  await queue({ op: 'patch', id, body: { title } });
}

/** Title / snippet / word count after editing (coalesced: only the latest is sent). */
export async function noteContent(id, { title, snippet, words, text, headings }, canEdit) {
  if (findDoc(id)?.kind === 'file') return;   // a Drive file's snippet/words are its address and size, not writing
  const patch = { snippet, words, text, headings, updatedAt: Date.now(), cachedAt: Date.now() };
  if (title !== undefined) { patch.title = title; patch.dirtyTitle = canEdit; }
  await patchLocal(id, patch);
  if (canEdit) await queue({ op: 'patch', id, body: { title, snippet, words } });
}

export async function trashDoc(id) {
  const d = findDoc(id);
  if (d?.role !== 'owner') {
    await S().dropDoc(id);
    docs.value = docs.value.filter(x => x.id !== id);
  } else {
    await patchLocal(id, { deletedAt: Date.now() });
  }
  await queue({ op: 'trash', id });
}

export async function restoreDoc(id) {
  await patchLocal(id, { deletedAt: null });
  await queue({ op: 'restore', id });
}

export async function deleteForever(id) {
  await api(`/api/docs/${id}/forever`, { method: 'POST' });
  await S().dropDoc(id);
  docs.value = docs.value.filter(x => x.id !== id);
}

export async function starDoc(id, starred) {
  await patchLocal(id, { starred });
  await queue({ op: 'star', id, body: { starred } });
}

/* ---------- groups: your own named groups of docs (worker/groups.js) ----------
   Kept on this device too (localStorage), changed right away here, and sent
   through the same outbox as everything else, so they work offline. */

const GKEY = 'dp:groups';
const readGroups = () => { try { return JSON.parse(localStorage.getItem(GKEY) || '[]'); } catch { return []; } };
export const groups = signal(readGroups());
function saveGroups(list) {
  groups.value = list.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
  try { localStorage.setItem(GKEY, JSON.stringify(groups.value)); } catch { /* full or private */ }
}

export async function createGroup(name) {
  const g = { id: newId(), name: (name || '').trim().slice(0, 60) || 'New group', createdAt: Date.now() };
  saveGroups([...groups.value, g]);
  await queue({ op: 'gnew', id: g.id, body: { id: g.id, name: g.name } });
  return g;
}

export async function renameGroup(id, name) {
  saveGroups(groups.value.map(g => (g.id === id ? { ...g, name } : g)));
  await queue({ op: 'gren', id, body: { name } });
}

export async function deleteGroup(id) {
  saveGroups(groups.value.filter(g => g.id !== id));
  for (const d of docs.value.filter(x => x.groupId === id)) await patchLocal(d.id, { groupId: null });
  await queue({ op: 'gdel', id });
}

/** Put docs in a group (null = take them out). */
export async function moveToGroup(ids, groupId) {
  for (const id of ids) {
    await patchLocal(id, { groupId });
    await queue({ op: 'group', id, body: { groupId } });
  }
}

/* ---------- the outbox ---------- */

async function queue(op) {
  const s = S();
  if (op.op === 'patch') {
    // only the latest title/snippet for a doc needs sending
    for (const o of await s.ops()) if (o.op === 'patch' && o.id === op.id) { op.body = { ...o.body, ...op.body }; await s.removeOp(o.k); }
  }
  await s.addOp(op);
  flushOutbox();
}

let flushing = null, again = false;
export function flushOutbox() {
  // something queued while a send is running goes right after it, not at the next refresh
  if (flushing) { again = true; return flushing; }
  flushing = doFlush().finally(() => {
    flushing = null;
    if (again) { again = false; flushOutbox(); }
  });
  return flushing;
}

async function doFlush() {
  const s = S();
  if (!s || !online.value) return;
  for (const o of await s.ops()) {
    try {
      if (o.op === 'create') {
        await api('/api/docs', { method: 'POST', body: o.body });
        await s.patchDocRec(o.id, { pendingCreate: false });
      } else if (o.op === 'patch') {
        await api(`/api/docs/${o.id}`, { method: 'PATCH', body: o.body });
        if (o.body.title !== undefined) await s.patchDocRec(o.id, { dirtyTitle: false });
      } else if (o.op === 'trash') {
        await api(`/api/docs/${o.id}`, { method: 'DELETE' });
      } else if (o.op === 'restore') {
        await api(`/api/docs/${o.id}/restore`, { method: 'POST' });
      } else if (o.op === 'star' || o.op === 'group') {
        await api(`/api/docs/${o.id}`, { method: 'PATCH', body: o.body });
      } else if (o.op === 'gnew') {
        await api('/api/groups', { method: 'POST', body: o.body });
      } else if (o.op === 'gren') {
        await api(`/api/groups/${o.id}`, { method: 'PATCH', body: o.body });
      } else if (o.op === 'gdel') {
        await api(`/api/groups/${o.id}`, { method: 'DELETE' });
      }
      await s.removeOp(o.k);
    } catch (err) {
      if (isOffline(err) || err.status >= 500 || err.status === 429) return;   // try again later
      console.warn('[outbox] dropped', o, err.message);
      await s.removeOp(o.k);   // the server said no for good (e.g. doc gone)
    }
  }
}

/** True once the server knows about this doc (its create has gone out). */
export async function isCreated(id) {
  await flushOutbox();
  return !(await S().ops()).some(o => o.op === 'create' && o.id === id);
}

/* ---------- background caching ---------- */

let prefetching = false;
/** Bring the newest docs onto this device so they open instantly and work offline. */
export async function prefetch(limit = 25) {
  const s = S();
  if (!s || prefetching || !online.value) return;
  prefetching = true;
  try {
    // Drive files hold no writing (their snippet is the file's address): summarising them would wipe it
    const list = docs.value.filter(d => !d.deletedAt && !d.pendingCreate && !d.local && d.kind !== 'file').slice(0, limit);
    for (const d of list) {
      if (d.cachedAt && d.cachedAt >= (d.updatedAt || 0)) continue;
      const meta = await s.getMeta(d.id);
      const since = meta?.lastSeq || 0;
      let bytes;
      try { bytes = await api(`/api/docs/${d.id}/updates?since=${since}`); } catch { continue; }
      const entries = unpackBundle(bytes);
      for (const e of entries) await s.addEntry(d.id, e.data.slice(), false);
      if (entries.length) await s.setMeta(d.id, { lastSeq: Math.max(since, ...entries.map(e => e.seq)) });
      // text for offline search
      const ydoc = new Y.Doc();
      for (const r of await s.entries(d.id)) { try { Y.applyUpdate(ydoc, r.data); } catch { /* skip */ } }
      const json = toJSON(ydoc);
      await patchLocal(d.id, { ...summary(json), title: titleOf(ydoc) || d.title, cachedAt: Date.now() });
      await new Promise(r => (window.requestIdleCallback ? requestIdleCallback(r, { timeout: 500 }) : setTimeout(r, 50)));
    }
  } finally { prefetching = false; }
}

/** A doc's whole content as a Yjs doc (this device's copy, topped up from the server). For folder sync. */
export async function loadForExport(id) {
  const s = S();
  if (online.value) {
    const meta = await s.getMeta(id);
    const since = meta?.lastSeq || 0;
    try {
      const entries = unpackBundle(await api(`/api/docs/${id}/updates?since=${since}`));
      for (const e of entries) await s.addEntry(id, e.data.slice(), false);
      if (entries.length) await s.setMeta(id, { lastSeq: Math.max(since, ...entries.map(e => e.seq)) });
    } catch { /* offline: use what's here */ }
  }
  const ydoc = new Y.Doc();
  for (const r of await s.entries(id)) { try { Y.applyUpdate(ydoc, r.data); } catch { /* skip */ } }
  return { ydoc };
}
